import React, { useEffect, useMemo, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import { Card } from "reactstrap";
import {
  Button,
  ConfigProvider,
  Empty,
  Input,
  Select,
  Skeleton,
  Space,
  Spin,
  message,
} from "antd";
import {
  FileExcelOutlined,
  FullscreenExitOutlined,
  FullscreenOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import Head from "../../../../layout/head/Head";
import Content from "../../../../layout/content/Content";
import { updateMenuData } from "../../../../store/slices/misc/menuData";
import TopMenu from "../../../../route/topMenu";
import { threeMReportApis } from "../../../../api/ThreeMReport";
import InternalTaskKpiCards from "./InternalTaskKpiCards";
import InternalTaskSummaryTable from "./InternalTaskSummaryTable";
import UnassignedTasksDrawer from "./UnassignedTasksDrawer";
import WbsCodeToggle from "../../shared/WbsCodeToggle";
import { useWbsName } from "../../shared/useWbsCodes";
import {
  InternalTaskGroup,
  InternalTaskSummaryData,
  num,
} from "./internalTaskShared";

// Internal Task Number Summary: tasks grouped by their internal task number
// (one group per number + UOM — mixed-UOM numbers arrive as multiple groups,
// rendered as-is) with weighted planned/executed rates and amounts. One fetch
// powers the KPI cards, the tree table AND the Excel export (tasks are
// embedded in the summary payload). Project context comes the same way as
// MachineryCostAnalysis: location.state { id, projectName } with the optional
// :projectId route-param fallback.

const InternalTaskSummaryReport = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const params = useParams();
  const { projectName: stateProjectName, id: stateProjectId } =
    (location.state as any) || {};
  const projectId = stateProjectId ?? params.projectId;

  const [data, setData] = useState<InternalTaskSummaryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Phase/Activity refetch server-side; search filters client-side.
  const [phaseId, setPhaseId] = useState<number | undefined>(undefined);
  const [activityId, setActivityId] = useState<number | undefined>(undefined);
  const [search, setSearch] = useState("");

  // Snapshot of the UNFILTERED fetch's groups — the Phase/Activity dropdown
  // options derive from its embedded task rows, so no separate options API is
  // ever called and filtered fetches never shrink the option lists.
  const [baseGroups, setBaseGroups] = useState<InternalTaskGroup[]>([]);

  const [refreshTick, setRefreshTick] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [unassignedOpen, setUnassignedOpen] = useState(false);
  const [exportingUnassigned, setExportingUnassigned] = useState(false);

  // Org branding for the Excel export. The currentUser slice stores the raw
  // /users/currentUser payload; orgName/orgLogo exist at runtime even though
  // the slice's local Data interface doesn't declare them.
  const currentUser = useSelector((state: any) => state.currentUser?.data);
  const orgName: string | undefined = currentUser?.orgName;
  const orgLogoUrl: string | undefined = currentUser?.orgLogo;

  // Export toasts go through a message instance whose holder lives inside the
  // report container — global message portals to document.body, which is
  // invisible while this container is fullscreen.
  const [msgApi, msgHolder] = message.useMessage();

  // Fullscreen: the report container goes fullscreen via the browser API.
  // Popups (Select dropdowns, tooltips) are re-targeted INTO the container —
  // portals left on document.body would be invisible while another element is
  // fullscreen. Same pattern as MachineryCostAnalysis / ThreeMConsolidation.
  const containerRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    // Covers both the button and ESC (the browser exits without telling us).
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen?.();
    } else {
      containerRef.current?.requestFullscreen?.().catch(() => {
        message.warning("Fullscreen is not supported in this browser");
      });
    }
  };

  const popupContainer = () => containerRef.current || document.body;

  // Project top-menu, same as WBSViewNew / MachineryCostAnalysis
  useEffect(() => {
    dispatch(
      updateMenuData(
        TopMenu?.projectNav?.map((item: any) => ({
          ...item,
          linkState: location.state,
        }))
      )
    );
  }, [dispatch, location]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!projectId) {
      setError("Project ID not found");
      setLoading(false);
      return;
    }
    let cancelled = false;
    const unfiltered = phaseId === undefined && activityId === undefined;
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await threeMReportApis.getInternalTaskSummary(
          projectId,
          { phaseId, activityId }
        );
        const { status, data: payload } = response || {};
        if (cancelled) return;
        if (status) {
          setData(payload || {});
          // Keep the filter-option snapshot in sync with the latest
          // unfiltered load (initial load is always unfiltered).
          if (unfiltered) setBaseGroups(payload?.groups || []);
        } else {
          setError(
            response?.message || "Failed to load Weighted Avg. Rate Summary"
          );
          message.error(
            response?.message || "Failed to load Weighted Avg. Rate Summary"
          );
        }
      } catch (err) {
        console.error("Error loading internal task summary:", err);
        if (!cancelled) {
          setError("Error loading Weighted Avg. Rate Summary");
          message.error("Error loading Weighted Avg. Rate Summary");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [projectId, phaseId, activityId, refreshTick]);

  const projectName = data?.projectName || stateProjectName || "Project";
  const groups = useMemo(() => data?.groups || [], [data]);

  // Client-side search: groups filtered by internal-task-number substring —
  // no refetch. The export intentionally ignores this (it takes `groups`).
  const filteredGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter((group) =>
      (group.internalTaskNumber || "").toLowerCase().includes(q)
    );
  }, [groups, search]);

  // Filter options from the unfiltered snapshot's embedded task rows.
  const wbsName = useWbsName();

  const phaseOptions = useMemo(() => {
    const seen = new Map<number, string>();
    baseGroups.forEach((group) =>
      (group.tasks || []).forEach((task) => {
        const id = num(task.phaseId);
        if (id > 0 && !seen.has(id)) {
          seen.set(id, wbsName(task.phaseName) || `Phase ${id}`);
        }
      })
    );
    return Array.from(seen, ([value, label]) => ({ value, label })).sort(
      (a, b) => a.label.localeCompare(b.label)
    );
  }, [baseGroups, wbsName]);

  const activityOptions = useMemo(() => {
    if (!phaseId) return [];
    const seen = new Map<number, string>();
    baseGroups.forEach((group) =>
      (group.tasks || []).forEach((task) => {
        if (num(task.phaseId) !== phaseId) return;
        const id = num(task.activityId);
        if (id > 0 && !seen.has(id)) {
          seen.set(id, wbsName(task.activityName) || `Activity ${id}`);
        }
      })
    );
    return Array.from(seen, ([value, label]) => ({ value, label })).sort(
      (a, b) => a.label.localeCompare(b.label)
    );
  }, [baseGroups, phaseId, wbsName]);

  const hasFilters = !!phaseId || !!activityId || search.trim() !== "";

  const clearFilters = () => {
    setPhaseId(undefined);
    setActivityId(undefined);
    setSearch("");
  };

  // Styled Excel export off the already-loaded groups (tasks are embedded in
  // the summary payload — no extra API call). The logo fetch is best-effort.
  const handleExport = async () => {
    if (!projectId || !data) return;
    setExporting(true);
    try {
      const helpers = await import("../shared/excelHelpers");
      const logo = await helpers.fetchLogoBase64(orgLogoUrl);
      const { default: exportWorkbook } = await import("./exportWorkbook");
      const phaseName = phaseId
        ? phaseOptions.find((o) => o.value === phaseId)?.label
        : undefined;
      const activityName =
        phaseId && activityId
          ? activityOptions.find((o) => o.value === activityId)?.label
          : undefined;
      await exportWorkbook({
        data: { ...data, projectName },
        groups,
        orgName,
        logo,
        filters: { phaseName, activityName },
      });
      msgApi.success("Excel exported");
    } catch (err) {
      console.error("Error exporting Weighted Avg. Rate Summary:", err);
      msgApi.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  // Unassigned drill-down export — same branding pipeline as the main export,
  // off the same loaded groups (the builder flattens the Unassigned ones).
  const handleExportUnassigned = async () => {
    if (!projectId || groups.length === 0) return;
    setExportingUnassigned(true);
    try {
      const helpers = await import("../shared/excelHelpers");
      const logo = await helpers.fetchLogoBase64(orgLogoUrl);
      const { default: exportUnassigned } = await import(
        "./exportUnassignedWorkbook"
      );
      const phaseName = phaseId
        ? phaseOptions.find((o) => o.value === phaseId)?.label
        : undefined;
      const activityName =
        phaseId && activityId
          ? activityOptions.find((o) => o.value === activityId)?.label
          : undefined;
      await exportUnassigned({
        projectName,
        groups,
        orgName,
        logo,
        filters: { phaseName, activityName },
      });
      msgApi.success("Excel exported");
    } catch (err) {
      console.error("Error exporting unassigned tasks:", err);
      msgApi.error("Export failed");
    } finally {
      setExportingUnassigned(false);
    }
  };

  return (
    <>
      <Head title="Weighted Avg. Rate Summary" />
      <Content>
        <div
          ref={containerRef}
          style={{
            position: "relative",
            ...(isFullscreen
              ? { background: "#f5f6fa", overflowY: "auto", padding: 16 }
              : {}),
          }}
        >
        <ConfigProvider getPopupContainer={popupContainer}>
        {msgHolder}
        <Card className="card-bordered" style={{ minHeight: "85vh" }}>
          <div className="card-inner card-inner-lg">
            {/* Header */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                flexWrap: "wrap",
                gap: 12,
                marginBottom: 16,
              }}
            >
              <div>
                <h5 style={{ fontWeight: 600, marginBottom: 2 }}>
                  Weighted Avg. Rate Summary
                </h5>
                <span style={{ color: "#8c8c8c", fontSize: 13 }}>
                  {projectName} · Weighted avg rates and amounts grouped by
                  internal task number
                </span>
              </div>
              <Space>
                <Button
                  icon={
                    isFullscreen ? (
                      <FullscreenExitOutlined />
                    ) : (
                      <FullscreenOutlined />
                    )
                  }
                  onClick={toggleFullscreen}
                >
                  {isFullscreen ? "Exit Full Screen" : "Full Screen"}
                </Button>
                <Button
                  icon={<FileExcelOutlined />}
                  loading={exporting}
                  disabled={loading || !data || groups.length === 0}
                  onClick={handleExport}
                >
                  Export
                </Button>
                <WbsCodeToggle />
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => setRefreshTick((t) => t + 1)}
                  disabled={loading}
                >
                  Refresh
                </Button>
              </Space>
            </div>

            {/* Filter bar: Phase/Activity refetch (server re-aggregates); the
                search filters the loaded groups client-side. */}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 10,
                alignItems: "center",
                marginBottom: 16,
              }}
            >
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Phase"
                style={{ width: 220 }}
                options={phaseOptions}
                value={phaseId}
                onChange={(value) => {
                  setPhaseId(value);
                  setActivityId(undefined);
                }}
              />
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Activity"
                style={{ width: 220 }}
                options={activityOptions}
                value={activityId}
                disabled={!phaseId}
                onChange={(value) => setActivityId(value)}
              />
              <Input.Search
                allowClear
                placeholder="Search internal task number"
                style={{ width: 260 }}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {hasFilters ? (
                <Button type="link" onClick={clearFilters}>
                  Clear filters
                </Button>
              ) : null}
            </div>

            {loading && !data ? (
              <Skeleton active paragraph={{ rows: 10 }} />
            ) : error && !data ? (
              <Empty description={error} style={{ padding: "60px 0" }} />
            ) : (
              <Spin spinning={loading}>
                <InternalTaskKpiCards
                  data={data}
                  onUnassignedClick={() => setUnassignedOpen(true)}
                />
                {groups.length === 0 ? (
                  <Empty
                    description={
                      phaseId || activityId
                        ? "No tasks match the current filters"
                        : "No tasks with rates for this project yet"
                    }
                    style={{ padding: "40px 0" }}
                  />
                ) : (
                  <InternalTaskSummaryTable
                    groups={filteredGroups}
                    loading={loading}
                  />
                )}
              </Spin>
            )}
          </div>
        </Card>

        <UnassignedTasksDrawer
          open={unassignedOpen}
          groups={groups}
          onClose={() => setUnassignedOpen(false)}
          getContainer={popupContainer}
          onExport={handleExportUnassigned}
          exporting={exportingUnassigned}
        />
        </ConfigProvider>
        </div>
      </Content>
    </>
  );
};

export default InternalTaskSummaryReport;
